import React, { useState, useEffect } from 'react';
import { Menu, X, Mail, Linkedin, Instagram, Music, ArrowRight, TrendingUp, Target, Send, Download, Briefcase, Calendar, DollarSign, MousePointer, Eye, Star, CheckCircle, Monitor, Palette, PenTool, BarChart3, Home } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';

// Use relative URLs on production (same-origin) to avoid CORS issues
const BACKEND_URL = window.location.hostname === 'sierrascala.com' || window.location.hostname === 'www.sierrascala.com'
  ? ''
  : process.env.REACT_APP_BACKEND_URL;

// PPC Case Study Data
const ppcCaseStudies = [
  {
    client: "Fort Worth Dental",
    website: "fortworthdental.com",
    industry: "Healthcare / Dental",
    challenge: "Previous agency was averaging $13 per click with only ~46 new patients per month. High ad spend with poor conversion tracking and campaigns not optimized for new patient acquisition.",
    solution: "Implemented time management optimization to show ads during peak appointment-booking hours. Built focused long-tail keyword campaigns specifically targeting new patient conversions and local dental intent searches.",
    color: "from-emerald-500 to-teal-500",
    results: [
      { metric: "130+", label: "New Patients/Mo" },
      { metric: "-81%", label: "Cost Per Click" },
      { metric: "$2.47", label: "Avg CPC" }
    ],
    chartData: {
      oneMonth: [
        { period: "Week 1", current: 28, previous: 11 },
        { period: "Week 2", current: 31, previous: 12 },
        { period: "Week 3", current: 33, previous: 11 },
        { period: "Week 4", current: 36, previous: 12 }
      ],
      threeMonth: [
        { period: "Month 1", current: 82, previous: 46 },
        { period: "Month 2", current: 105, previous: 44 },
        { period: "Month 3", current: 118, previous: 48 }
      ],
      sixMonth: [
        { period: "Jan", current: 82, previous: 46 },
        { period: "Feb", current: 94, previous: 44 },
        { period: "Mar", current: 105, previous: 48 },
        { period: "Apr", current: 112, previous: 45 },
        { period: "May", current: 122, previous: 47 },
        { period: "Jun", current: 130, previous: 46 }
      ]
    },
    beforeAfter: {
      before: { cpc: "$13.00", newPatients: "~46/mo", tracking: "Poor", focus: "Generic" },
      after: { cpc: "$2.47", newPatients: "130+/mo", tracking: "Full", focus: "New Patients" }
    }
  },
  {
    client: "Lazy Days Home",
    website: "lazydayshome.com",
    industry: "Home Cleaning Services",
    location: "Arlington, TX",
    challenge: "Overspending on Google Ads at $2,500/month with $12 average cost per click. Poor geographic targeting was wasting budget on irrelevant clicks outside their service area.",
    solution: "Implemented geotargeted long-tail keywords focused on Arlington and DFW regions. Optimized ad scheduling and refined audience targeting to dramatically improve efficiency and boost organic search visibility.",
    color: "from-blue-500 to-indigo-500",
    results: [
      { metric: "+120%", label: "Better Results" },
      { metric: "-87%", label: "Cost Per Click" },
      { metric: "-80%", label: "Ad Budget" }
    ],
    chartData: {
      oneMonth: [
        { period: "Week 1", current: 15, previous: 9 },
        { period: "Week 2", current: 19, previous: 10 },
        { period: "Week 3", current: 22, previous: 8 },
        { period: "Week 4", current: 26, previous: 11 }
      ],
      threeMonth: [
        { period: "Month 1", current: 38, previous: 19 },
        { period: "Month 2", current: 52, previous: 21 },
        { period: "Month 3", current: 64, previous: 18 }
      ],
      sixMonth: [
        { period: "Jan", current: 38, previous: 19 },
        { period: "Feb", current: 45, previous: 21 },
        { period: "Mar", current: 52, previous: 18 },
        { period: "Apr", current: 58, previous: 20 },
        { period: "May", current: 68, previous: 19 },
        { period: "Jun", current: 74, previous: 21 }
      ]
    },
    beforeAfter: {
      before: { cpc: "$12.00", budget: "$2,500/mo", leads: "~19/mo", organic: "Low" },
      after: { cpc: "$1.59", budget: "$500/mo", leads: "~42/mo", organic: "High" }
    }
  }
];

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg">
        <p className="font-semibold text-sm mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Main Portfolio App Component
function PortfolioApp() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formType, setFormType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    yourTitle: '',
    hiringPosition: '',
    positionType: '',
    location: '',
    agencyName: '',
    clientCompany: '',
    compensationRange: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');
  const [selectedBlogPost, setSelectedBlogPost] = useState(null);
  const [isBlogModalOpen, setIsBlogModalOpen] = useState(false);
  const [selectedPPCStudy, setSelectedPPCStudy] = useState(0);
  const [ppcTimeframe, setPpcTimeframe] = useState('sixMonth');
  const [dynamicBlogPosts, setDynamicBlogPosts] = useState([]);
  const [loadingBlog, setLoadingBlog] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [exitPopupShown, setExitPopupShown] = useState(false);

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitPopupShown) {
        setShowExitPopup(true);
        setExitPopupShown(true);
      }
    };

    document.addEventListener('mouseout', handleMouseLeave);
    return () => document.removeEventListener('mouseout', handleMouseLeave);
  }, [exitPopupShown]);

  // Fetch blog posts from API
  useEffect(() => {
    const fetchBlogPosts = async () => {
      setLoadingBlog(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/blog?limit=6`);
        if (response.ok) {
          const posts = await response.json();
          if (posts.length > 0) {
            setDynamicBlogPosts(posts);
          }
        }
      } catch (error) {
        console.log('Using static blog posts');
      }
      setLoadingBlog(false);
    };
    fetchBlogPosts();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'proposals', 'portfolio', 'ppc-results', 'content-creation', 'skills', 'social-media-tips', 'schedule-meet', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          formType: formType
        }),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          yourTitle: '',
          hiringPosition: '',
          positionType: '',
          location: '',
          agencyName: '',
          clientCompany: '',
          compensationRange: '',
          message: ''
        });
        setTimeout(() => setFormStatus(''), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus(''), 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus(''), 3000);
    }
  };

  const experiences = [
    {
      company: 'Your Company Name',
      position: 'Social Media Marketing Specialist',
      type: 'Remote',
      duration: 'January 2023 - Present',
      location: 'Dallas, TX',
      description: 'Leading social media strategy and content creation across multiple platforms, driving engagement and brand awareness.',
      achievements: [
        'Increased overall social media engagement by 180% year-over-year',
        'Managed content calendar and campaigns for 5+ social platforms',
        'Collaborated with cross-functional teams to align social strategy with business goals'
      ]
    },
  ];

  const caseStudies = [
    {
      title: 'LinkedIn B2B Campaign',
      company: 'Tech Startup',
      role: 'Social Media Strategist',
      tools: ['LinkedIn Ads Manager', 'Canva', 'Hootsuite', 'Google Analytics', 'WTS Social Builder', 'WTS Turbo SEO'],
      challenge: 'Low engagement on LinkedIn posts with minimal reach to target B2B audience',
      strategy: [
        'Created weekly carousel posts with industry insights',
        'Implemented employee advocacy program',
        'Used targeted hashtags and optimal posting times'
      ],
      results: [
        { metric: '250%', label: 'Engagement Increase' },
        { metric: '45', label: 'Qualified Leads' },
        { metric: '3.2K', label: 'New Followers' }
      ],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Instagram Growth Campaign',
      company: 'E-commerce Brand',
      role: 'Content Creator & Campaign Manager',
      tools: ['Meta Business Suite', 'Canva', 'Adobe Photoshop', 'Later', 'WTS Social Builder', 'WTS Turbo SEO'],
      challenge: 'Stagnant follower growth and low conversion rates from social traffic',
      strategy: [
        'Launched UGC campaign with branded hashtag',
        'Created Reels featuring product use cases',
        'Collaborated with micro-influencers'
      ],
      results: [
        { metric: '180%', label: 'Follower Growth' },
        { metric: '65%', label: 'Higher CTR' },
        { metric: '12K', label: 'UGC Submissions' }
      ],
      color: 'from-pink-500 to-purple-500'
    },
    {
      title: 'TikTok Viral Strategy',
      company: 'Dallas Baptist University Event',
      role: 'Social Media Lead',
      tools: ['TikTok Business', 'CapCut', 'Canva', 'Excel Analytics', 'WTS Social Builder', 'WTS Turbo SEO'],
      challenge: 'Need to drive awareness and ticket sales for university event among Gen Z',
      strategy: [
        'Trend-jacking with campus-specific humor',
        'Behind-the-scenes content series',
        'Student ambassador program'
      ],
      results: [
        { metric: '500K', label: 'Total Views' },
        { metric: '8.5K', label: 'Event Attendees' },
        { metric: '95%', label: 'Ticket Sellout' }
      ],
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Workflow Migration Project',
      company: 'Investment Management Firm',
      role: 'Project Coordinator & Data Migration Lead',
      tools: ['Smartsheet', 'Asana', 'Microsoft Excel', 'PowerPoint'],
      challenge: 'Marketing team struggling with scattered workflows across spreadsheets, needing centralized project management',
      strategy: [
        'Audited existing Smartsheet workflows and data structures',
        'Mapped tasks, dependencies, and custom fields to Asana',
        'Created project templates mirroring existing processes',
        'Validated data integrity and trained end users'
      ],
      results: [
        { metric: '100%', label: 'Data Migrated' },
        { metric: '40%', label: 'Time Savings' },
        { metric: '3 Weeks', label: 'Completion' }
      ],
      color: 'from-emerald-500 to-teal-500'
    }
  ];

  const skills = [
    { category: 'Social Platforms', items: ['Instagram', 'TikTok', 'LinkedIn', 'Facebook', 'Twitter/X', 'Pinterest', 'YouTube'] },
    { category: 'Design & Creative', items: ['Canva (Expert)', 'Adobe Photoshop', 'Adobe Illustrator', 'Adobe InDesign', 'CapCut', 'Figma'] },
    { category: 'Marketing Skills', items: ['Content Strategy', 'Copywriting', 'Community Management', 'Analytics & Reporting', 'Trend Analysis', 'A/B Testing', 'Campaign Management'] },
    { category: 'Project Management', items: ['Asana', 'Smartsheet', 'Monday.com', 'ClickUp', 'Trello', 'Airtable', 'Microsoft 365'] }
  ];

  // Testimonials data
  const testimonials = [
    {
      name: "Elizabeth Barefoot",
      role: "Owner",
      company: "1st Avenue Ice Cream & Pastry",
      image: null,
      quote: "Sierra's video content completely transformed our social media presence. The videos she created for us not only look amazing but actually drove real customers to our shop. We even had a TikTok influencer come in because of her work! Couldn't be happier with the results.",
      rating: 5
    },
    {
      name: "Lisa Cowart",
      role: "Office Manager",
      company: "Fort Worth Dental",
      image: null,
      quote: "Our Google Ads were bleeding money before Sierra took over. She cut our cost per click by over 80% while nearly tripling our new patient inquiries. The ROI has been incredible—we went from 46 new patients a month to over 130. Best marketing investment we've made.",
      rating: 5
    }
  ];

  // Job Application Proposals - Add new proposals here for each job application
  const jobProposals = [
    {
      company: 'Megatel Homes',
      role: 'Digital Marketing Strategist',
      industry: 'Homebuilding',
      description: 'Custom marketing strategy for Texas\'s innovative lagoon community builder',
      link: '/megatel',
      color: 'from-amber-500 to-orange-500',
      active: false
    },
    {
      company: 'Meadows Institute',
      role: 'Social Media & Content Manager',
      industry: 'Mental Health Policy',
      description: 'Social media strategy & content plan for Texas\'s leading mental health policy nonprofit',
      link: '/meadows',
      color: 'from-teal-500 to-cyan-500',
      active: false
    },
    {
      company: 'Opt Digital',
      role: 'Local SEO Specialist',
      industry: 'Digital Marketing Agency',
      description: 'Local SEO strategy & client delivery plan for a fast-growing agency serving trades & service businesses',
      link: '/optdigital',
      color: 'from-emerald-500 to-green-600',
      active: false
    }
    // Add more job proposals here as needed:
    // {
    //   company: 'Company Name',
    //   role: 'Position Title',
    //   industry: 'Industry',
    //   description: 'Brief description of the proposal',
    //   link: '/company-slug',
    //   color: 'from-blue-500 to-cyan-500',
    //   active: true
    // }
  ];

  const currentYear = new Date().getFullYear();

  const blogPosts = [
    {
      title: '5 TikTok Trends B2B Brands Should Embrace in 2025',
      excerpt: 'TikTok isn\'t just for dance videos anymore. Here\'s how B2B companies can leverage viral trends to humanize their brand and reach decision-makers.',
      date: 'November 15, 2025',
      readTime: '5 min read',
      content: `
        <p>TikTok has evolved far beyond dance challenges and lip-sync videos. In 2025, it's become a powerful platform for B2B marketing, with decision-makers spending an average of 52 minutes per day on the app. Here are five trends your B2B brand should embrace:</p>

        <h3>1. Educational Micro-Content</h3>
        <p>Break down complex industry topics into 15-30 second videos. Think "Tech Tips Tuesday" or "Quick Win Wednesdays." The algorithm loves consistency, and your audience loves actionable insights they can implement immediately.</p>

        <h3>2. Behind-the-Scenes Culture Content</h3>
        <p>Humanize your brand by showing your team, office culture, and day-to-day operations. This builds trust and makes your company relatable—crucial for B2B relationships.</p>

        <h3>3. Trend-Jacking with Industry Context</h3>
        <p>Take viral trends and adapt them to your industry. That popular sound? Use it to explain your product. That dance trend? Turn it into a process demonstration. It shows you're current while staying professional.</p>

        <h3>4. Customer Success Stories</h3>
        <p>Short testimonials and case study snippets perform incredibly well. Real clients sharing real results builds credibility faster than any marketing copy.</p>

        <h3>5. Collaborative Content with Clients</h3>
        <p>Partner with your customers to create duet videos or collaborative content. It's social proof that feels organic and reaches both your audiences.</p>

        <p><strong>The Bottom Line:</strong> TikTok isn't about going viral—it's about consistent, authentic engagement with your target audience. Start small, test what resonates, and iterate based on analytics.</p>
      `
    },
    {
      title: 'Why Authenticity Beats Algorithm Every Time',
      excerpt: 'In an era of AI-generated content, authentic human connection is your competitive advantage. Learn how to build genuine engagement that converts.',
      date: 'November 8, 2025',
      readTime: '4 min read',
      content: `
        <p>We're living in the age of AI-generated everything. ChatGPT can write your captions, DALL-E can create your images, and countless tools promise to automate your entire social media presence. But here's what the algorithms won't tell you: authenticity still wins.</p>

        <h3>The Authenticity Paradox</h3>
        <p>As AI-generated content floods social media, audiences are becoming increasingly savvy at spotting the difference between real and synthetic. That polished, perfect post might get likes, but it's the raw, authentic moments that build real communities.</p>

        <h3>What Authenticity Actually Means</h3>
        <p>Authenticity isn't about being unfiltered or unprofessional. It's about:</p>
        <ul>
          <li><strong>Showing your values:</strong> Take stands on issues that matter to your brand</li>
          <li><strong>Admitting mistakes:</strong> When something goes wrong, own it publicly</li>
          <li><strong>Sharing the journey:</strong> Not just the highlights, but the process</li>
          <li><strong>Being consistent:</strong> Your brand voice should be recognizable across all platforms</li>
        </ul>

        <h3>How to Build Authentic Engagement</h3>
        <p><strong>1. Respond to comments personally.</strong> Don't use canned responses. People can tell.</p>
        <p><strong>2. Share user-generated content.</strong> Your customers are your best marketers.</p>
        <p><strong>3. Go live regularly.</strong> Unedited, real-time content builds trust faster than anything else.</p>
        <p><strong>4. Tell real stories.</strong> Case studies, customer journeys, employee spotlights—these resonate.</p>

        <h3>The Competitive Advantage</h3>
        <p>While your competitors are optimizing for algorithms, you can optimize for humans. Build real relationships. Create content that sparks conversations. Be memorable for the right reasons.</p>

        <p>Because at the end of the day, algorithms change. Platforms evolve. But authentic human connection? That's timeless.</p>
      `
    },
    {
      title: 'The ROI of Social Media: Metrics That Actually Matter',
      excerpt: 'Vanity metrics won\'t impress stakeholders. Discover which KPIs demonstrate real business impact and how to track them effectively.',
      date: 'October 28, 2025',
      readTime: '6 min read',
      content: `
        <p>Your boss doesn't care about your follower count. There, I said it. What they care about is how social media contributes to the bottom line. Here's how to prove it.</p>

        <h3>Vanity Metrics vs. Value Metrics</h3>
        <p><strong>Vanity Metrics</strong> (look good, mean little):</p>
        <ul>
          <li>Total followers</li>
          <li>Post likes</li>
          <li>Impressions without context</li>
        </ul>

        <p><strong>Value Metrics</strong> (actually matter):</p>
        <ul>
          <li>Engagement rate (not just likes, but shares and saves)</li>
          <li>Click-through rate to website</li>
          <li>Conversion rate from social traffic</li>
          <li>Customer acquisition cost via social</li>
          <li>Customer lifetime value from social leads</li>
        </ul>

        <h3>The Metrics Framework</h3>
        <p><strong>Awareness Metrics:</strong></p>
        <ul>
          <li>Reach and impressions (with trend analysis)</li>
          <li>Share of voice in your industry</li>
          <li>Branded search lift</li>
        </ul>

        <p><strong>Engagement Metrics:</strong></p>
        <ul>
          <li>Engagement rate = (Likes + Comments + Shares + Saves) / Reach × 100</li>
          <li>Response rate and response time</li>
          <li>Video completion rate</li>
        </ul>

        <p><strong>Conversion Metrics:</strong></p>
        <ul>
          <li>Click-through rate (CTR)</li>
          <li>Conversion rate per platform</li>
          <li>Cost per acquisition (CPA)</li>
          <li>Return on ad spend (ROAS)</li>
        </ul>

        <p><strong>Retention Metrics:</strong></p>
        <ul>
          <li>Customer retention rate from social</li>
          <li>Repeat purchase rate</li>
          <li>Customer lifetime value (CLV)</li>
        </ul>

        <h3>How to Track Effectively</h3>
        <p>1. <strong>Set up UTM parameters</strong> for all social links to track traffic sources in Google Analytics</p>
        <p>2. <strong>Use platform analytics</strong> (Meta Business Suite, LinkedIn Analytics, TikTok Analytics)</p>
        <p>3. <strong>Create a dashboard</strong> that updates automatically (Google Data Studio or similar)</p>
        <p>4. <strong>Report monthly</strong> with trend analysis, not just numbers</p>

        <h3>Telling the Story</h3>
        <p>Numbers alone won't convince stakeholders. Connect the dots:</p>
        <ul>
          <li>"Our LinkedIn campaign generated 45 qualified leads, resulting in 8 closed deals worth $127K"</li>
          <li>"Instagram engagement increased 180%, driving 2,400 website visits and 92 email signups"</li>
        </ul>

        <p>This is how you prove social media ROI—and secure your budget for next quarter.</p>
      `
    },
    {
      title: '5 Social Media Strategies for Homebuilders in 2025',
      excerpt: 'The homebuilding industry is ripe for digital disruption. Here\'s how innovative builders can leverage social media and PPC to connect with prospective buyers.',
      date: 'December 1, 2025',
      readTime: '7 min read',
      content: `
        <p>The homebuilding industry has traditionally relied on model homes, billboards, and real estate agents for marketing. But in 2025, prospective homebuyers are researching online long before they visit a sales center. Here's how homebuilders can capture that digital attention.</p>

        <h3>1. Showcase the Lifestyle, Not Just the House</h3>
        <p>Today's homebuyers aren't just buying square footage—they're buying a lifestyle. Innovative builders understand this with amenity-rich communities that offer resort-style living at affordable prices. Create content that sells the dream:</p>
        <ul>
          <li><strong>Drone footage</strong> of community amenities (pools, parks, recreation areas)</li>
          <li><strong>Day-in-the-life Reels</strong> showing families enjoying the community</li>
          <li><strong>"Move-in day" content</strong> showing happy homeowners with keys in hand</li>
          <li><strong>Time-lapse videos</strong> of homes being built (incredibly satisfying to watch)</li>
        </ul>
        <p>This content humanizes your brand and builds emotional connection with prospective buyers.</p>

        <h3>2. Geo-Targeted Google Ads for High-Intent Searches</h3>
        <p>When someone searches "new homes in [city]" or "master-planned communities DFW," they're ready to buy. Capture that intent:</p>
        <ul>
          <li>Target long-tail keywords specific to your communities and school districts</li>
          <li>Create landing pages for each community highlighting the school district</li>
          <li>Highlight incentives prominently (rate buydowns, closing cost assistance)</li>
          <li>Implement call tracking to measure lead quality</li>
        </ul>

        <h3>3. Tell Your Founder Story</h3>
        <p>People connect with people, not corporations. The best homebuilder marketing tells a human story. Share:</p>
        <ul>
          <li>The founders' journey and vision for the company</li>
          <li>Why they're passionate about helping families own homes</li>
          <li>Innovative programs that help renters become buyers</li>
        </ul>

        <h3>4. Virtual Tours & Video Walkthroughs</h3>
        <p>Post-pandemic, buyers expect to preview homes online. Invest in:</p>
        <ul>
          <li>Professional video walkthroughs posted on YouTube and social</li>
          <li>360-degree virtual tours for your website</li>
          <li>Live video tours on Facebook/Instagram for new listings</li>
          <li>Model home park walkthrough content</li>
        </ul>

        <h3>5. TikTok & Reels for First-Time Buyers</h3>
        <p>First-time homebuyers are heavily influenced by social media. Create:</p>
        <ul>
          <li>"I can't believe this is real" tours of amazing amenities</li>
          <li>Price comparison content ("What $319K gets you in [suburb]")</li>
          <li>Behind-the-scenes of the building process</li>
          <li>Homeowner testimonials and move-in celebrations</li>
        </ul>

        <h3>The Competitive Advantage</h3>
        <p>Most homebuilders are still marketing like it's 2010. Innovative builders who embrace social media and digital advertising can reach buyers earlier in their journey, build trust through content, and ultimately close more sales. The builders who win in 2025 will be the ones who meet buyers where they are: online.</p>
      `
    }
  ];

  const openBlogModal = (post) => {
    setSelectedBlogPost(post);
    setIsBlogModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeBlogModal = () => {
    setIsBlogModalOpen(false);
    setSelectedBlogPost(null);
    document.body.style.overflow = 'unset';
  };

  const currentPPCStudy = ppcCaseStudies[selectedPPCStudy];
  const currentChartData = currentPPCStudy.chartData[ppcTimeframe];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => scrollToSection('home')}
              className="text-2xl font-bold bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent"
              data-testid="logo-button"
            >
              Sierra Scala
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-6">
              {['Home', 'About', 'Portfolio', 'PPC Results', 'Content Creation', 'Skills', 'Proposals', 'Social Media Tips', 'Schedule Meet', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replaceAll(' ', '-'))}
                  className={`text-sm font-medium transition-colors ${
                    activeSection === item.toLowerCase().replaceAll(' ', '-')
                      ? 'text-purple-600'
                      : 'text-gray-700 hover:text-purple-600'
                  }`}
                  data-testid={`nav-${item.toLowerCase().replaceAll(' ', '-')}`}
                >
                  {item}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-3">
              {['Home', 'About', 'Portfolio', 'PPC Results', 'Content Creation', 'Skills', 'Proposals', 'Social Media Tips', 'Schedule Meet', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replaceAll(' ', '-'))}
                  className="block w-full text-left px-3 py-2 rounded-lg text-gray-700 hover:bg-purple-50 hover:text-purple-600"
                  data-testid={`mobile-nav-${item.toLowerCase().replaceAll(' ', '-')}`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight" data-testid="hero-title">
                Turning Scrolls into
                <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent"> Sales</span>
              </h1>
              <p className="text-sm font-semibold text-purple-600 tracking-wider uppercase">Content Creator • Digital Marketer • PPC Specialist</p>
              <p className="text-lg md:text-xl text-gray-600" data-testid="hero-subtitle">
                Social Media Marketing & Google PPC Specialist | Instagram, TikTok, Meta, LinkedIn & Google Ads Expert | Dallas - Fort Worth, TX
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => scrollToSection('portfolio')}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all flex items-center gap-2"
                  data-testid="view-work-button"
                >
                  View My Work
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="px-8 py-3 border-2 border-purple-600 text-purple-600 rounded-full font-semibold hover:bg-purple-50 transition-all"
                  data-testid="hire-me-button"
                >
                  Let's Connect
                </button>
              </div>
              <div className="flex gap-4 pt-4">
                <a href="https://www.linkedin.com/in/sierra-scala-62b9a0254" target="_blank" rel="noopener noreferrer" className="p-3 rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-110" data-testid="linkedin-link">
                  <Linkedin className="text-blue-600" size={24} />
                </a>
              </div>
            </div>
            <div className="relative" data-testid="hero-image-container">
              <div className="relative z-10">
                <img
                  src="https://customer-assets.emergentagent.com/job_4e444d77-5918-4709-9ad3-5340553427e3/artifacts/n8ifnx3j_image.png"
                  alt="Sierra Scala - Social Media Marketing & Google PPC Specialist - Professional headshot"
                  className="rounded-2xl shadow-2xl w-full max-w-md mx-auto"
                  loading="eager"
                  width="400"
                  height="400"
                  data-testid="profile-image"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-3xl opacity-30 animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full blur-3xl opacity-30 animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="about-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12" data-testid="about-title">
            About <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Me</span>
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p data-testid="about-paragraph-1">
              Hi! I'm Sierra Scala — <strong>5+ years of professional experience in social media strategy, content creation, video production, communications, and data-driven marketing</strong>. Currently serving as <strong>Marketing Manager at Ninja Technologies</strong> (Remote Contract), with a <strong>BBA in Marketing from Dallas Baptist University</strong> (May 2023), I've helped brands of all sizes grow their digital presence and drive measurable results.
            </p>
            <p data-testid="about-paragraph-2">
              My approach combines <strong>creative storytelling with data-driven strategy</strong>. I don't just post content — I craft campaigns that resonate with target audiences and deliver ROI. From viral TikTok content (500K+ views) to high-converting Google Ads campaigns, I understand how to tailor messaging for each platform and audience. <strong>Proficient in social media management platforms</strong> (Hootsuite, Later, Meta Business Suite) and analytics dashboards — a quick learner who adopts new tools like Sprout Social rapidly.
            </p>
            <p data-testid="about-paragraph-3">
              I thrive in collaborative environments and have <strong>excellent interpersonal skills</strong> developed through cross-departmental coordination on marketing initiatives. Whether working with design teams, sales departments, or external vendors, I ensure projects move efficiently from concept to completion.
            </p>
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-200" data-testid="complex-storytelling">
              <p className="font-semibold text-teal-900 flex items-center gap-2">
                <PenTool size={20} />
                Simplifying Complex Information for Diverse Audiences
              </p>
              <p className="text-gray-700 mt-2">Specialized in translating complex topics into clear, audience-friendly narratives — from technical healthcare services (Fort Worth Dental) and business operations to policy-level storytelling. Passionate about impactful communications ready for public health, policy, and mission-driven content.</p>
            </div>
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200" data-testid="industry-interest">
              <p className="font-semibold text-amber-900 flex items-center gap-2">
                <Target size={20} />
                Industry Focus
              </p>
              <p className="text-gray-700 mt-2">From innovative homebuilders to mental health policy nonprofits — I bring targeted social media, PPC expertise, and visual storytelling to help organizations stand out. Created Reels and multimedia content generating <strong>127K+ video views and 68% foot-traffic lift</strong>. Eager to apply these skills to mission-driven organizations making real impact.</p>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100" data-testid="fun-fact">
              <p className="font-semibold text-purple-900">💡 Fun Fact:</p>
              <p className="text-gray-700 mt-2">I once grew a campus event's Instagram following by 300% in just 6 weeks using strategic UGC campaigns and trend-jacking—resulting in the highest-attended event in the program's history!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Proposals Section */}
      {jobProposals.filter(p => p.active).length > 0 && (
        <section id="proposals" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50" data-testid="proposals-section">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-purple-600 tracking-widest uppercase mb-4">For Recruiters</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Custom <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Proposals</span>
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Tailored marketing strategies created specifically for companies I'm excited to work with.
              </p>
            </div>

            <div className="grid gap-6">
              {jobProposals.filter(p => p.active).map((proposal, index) => (
                <a
                  key={index}
                  href={proposal.link}
                  className="block bg-white rounded-2xl p-6 border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all group"
                  data-testid={`proposal-${index}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 bg-gradient-to-r ${proposal.color} rounded-xl flex items-center justify-center`}>
                        <Briefcase className="text-white" size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">{proposal.company}</h3>
                        <p className="text-purple-500 text-sm">{proposal.role} • {proposal.industry}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 text-sm hidden md:block">{proposal.description}</span>
                      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-white font-semibold text-sm group-hover:shadow-lg transition-all">
                        View Proposal <ArrowRight size={16} />
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            <p className="text-center text-gray-500 text-sm mt-8">
              Interested in a custom proposal for your company? <a href="#contact" className="text-purple-600 hover:text-purple-500">Let's talk →</a>
            </p>
          </div>
        </section>
      )}

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4" data-testid="portfolio-title">
            Featured <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Work</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Real campaigns, real results. Here's how I've helped brands achieve their marketing goals.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-2 flex flex-col" data-testid={`case-study-${index}`}>
                <div className={`h-2 bg-gradient-to-r ${study.color}`}></div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-bold" data-testid={`case-study-title-${index}`}>{study.title}</h3>
                    {study.isSpec && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium whitespace-nowrap">SPEC PROJECT</span>
                    )}
                    {study.isProposal && (
                      <span className="text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-1 rounded-full font-medium whitespace-nowrap">PROPOSAL</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mb-1">{study.company}</p>
                  <p className="text-xs text-purple-600 font-medium mb-3">{study.role}</p>

                  <div className="mb-3">
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm">Challenge:</h4>
                    <p className="text-gray-600 text-xs">{study.challenge}</p>
                  </div>

                  <div className="mb-3">
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm">Strategy:</h4>
                    <ul className="space-y-1">
                      {study.strategy.map((item, i) => (
                        <li key={i} className="text-gray-600 text-xs flex items-start gap-2">
                          <span className="text-purple-500 mt-0.5">→</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Tools Used */}
                  <div className="mb-3">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Tools Used:</h4>
                    <div className="flex flex-wrap gap-1">
                      {study.tools.map((tool, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{tool}</span>
                      ))}
                    </div>
                  </div>

                  <div className="border-t pt-3 mt-auto">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Results:</h4>
                    <div className="grid grid-cols-3 gap-1">
                      {study.results.map((result, i) => (
                        <div key={i} className="text-center">
                          <div className={`text-lg font-bold bg-gradient-to-r ${study.color} bg-clip-text text-transparent`}>
                            {result.metric}
                          </div>
                          <div className="text-xs text-gray-500">{result.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overall Career Metrics Dashboard */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900" data-testid="metrics-dashboard">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-purple-300 tracking-widest uppercase mb-2">Career Impact</p>
            <h2 className="text-3xl font-bold text-white">Results That Speak for Themselves</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">850K+</div>
              <div className="text-purple-200 text-sm">Total Reach Generated</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">200+</div>
              <div className="text-purple-200 text-sm">Qualified Leads Delivered</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">-75%</div>
              <div className="text-purple-200 text-sm">Avg. Cost Per Click Reduction</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">15+</div>
              <div className="text-purple-200 text-sm">Successful Campaigns</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-purple-600 tracking-widest uppercase mb-4">Client Feedback</p>
            <h2 className="text-4xl font-bold text-gray-900">
              What Clients <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Say</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100 shadow-lg" data-testid={`testimonial-${index}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}, {testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW: Google PPC Results Section */}
      <section id="ppc-results" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-100 via-purple-50 to-blue-50" data-testid="ppc-results-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-purple-600 tracking-widest uppercase mb-4">Google Ads Expertise</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-testid="ppc-title">
              Google PPC <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Results</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Data-driven Google Ads campaigns that deliver measurable ROI for local businesses. Real results, verified data.
            </p>
          </div>

          {/* Client Selector */}
          <div className="flex justify-center gap-4 mb-8">
            {ppcCaseStudies.map((study, index) => (
              <button
                key={index}
                onClick={() => setSelectedPPCStudy(index)}
                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                  selectedPPCStudy === index
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-purple-50 border border-gray-200'
                }`}
                data-testid={`ppc-client-btn-${index}`}
              >
                {study.client}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Left: Client Info */}
            <div className="bg-white rounded-2xl p-8 border border-purple-200 shadow-lg">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{currentPPCStudy.client}</h3>
                <a 
                  href={`https://${currentPPCStudy.website}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 border border-purple-300 rounded-full text-purple-700 hover:text-purple-800 text-sm font-medium transition-all"
                  data-testid={`ppc-website-link-${currentPPCStudy.website}`}
                >
                  Visit {currentPPCStudy.website}
                  <span>↗</span>
                </a>
                <p className="text-gray-500 text-sm mt-3">{currentPPCStudy.industry}{currentPPCStudy.location ? ` • ${currentPPCStudy.location}` : ''}</p>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1">Challenge</p>
                  <p className="text-gray-700 text-sm">{currentPPCStudy.challenge}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1">Solution</p>
                  <p className="text-gray-700 text-sm">{currentPPCStudy.solution}</p>
                </div>
              </div>

              {/* Results Grid */}
              <div className="grid grid-cols-3 gap-3">
                {currentPPCStudy.results.map((result, i) => (
                  <div key={i} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 text-center border border-purple-100">
                    <div className={`text-2xl md:text-3xl font-bold bg-gradient-to-r ${currentPPCStudy.color} bg-clip-text text-transparent`}>
                      {result.metric}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">{result.label}</div>
                  </div>
                ))}
              </div>


              {/* Before/After Table */}
              <div className="mt-6 bg-purple-50 rounded-xl p-4 border border-purple-200">
                <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-3">Before vs After</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Before</p>
                    {Object.entries(currentPPCStudy.beforeAfter.before).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm py-1 border-b border-purple-100">
                        <span className="text-gray-600 capitalize">{key}</span>
                        <span className="text-gray-400 line-through">{value}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-2">After</p>
                    {Object.entries(currentPPCStudy.beforeAfter.after).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm py-1 border-b border-purple-100">
                        <span className="text-gray-600 capitalize">{key}</span>
                        <span className="text-emerald-600 font-semibold">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Chart */}
            <div className="bg-white rounded-2xl p-8 border border-purple-200 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-lg font-semibold text-gray-900">Performance Over Time</h4>
                <div className="flex gap-2">
                  {['oneMonth', 'threeMonth', 'sixMonth'].map((tf) => (
                    <button
                      key={tf}
                      onClick={() => setPpcTimeframe(tf)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        ppcTimeframe === tf
                          ? 'bg-purple-600 text-white'
                          : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                      data-testid={`ppc-timeframe-${tf}`}
                    >
                      {tf === 'oneMonth' ? '1 Mo' : tf === 'threeMonth' ? '3 Mo' : '6 Mo'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="h-80" data-testid="ppc-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={currentChartData}>
                    <defs>
                      <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#9333ea" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#9333ea" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorPrevious" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#d1d5db" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#d1d5db" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="period" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="previous"
                      name="Previous"
                      stroke="#6b7280"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      fill="url(#colorPrevious)"
                    />
                    <Area
                      type="monotone"
                      dataKey="current"
                      name="With Sierra Scala"
                      stroke="#9333ea"
                      strokeWidth={3}
                      fill="url(#colorCurrent)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                {selectedPPCStudy === 0 ? 'Monthly leads generated' : 'Monthly leads generated'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Creation Section - Ice Cream Videos */}
      <section id="content-creation" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="content-creation-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-pink-600 tracking-widest uppercase mb-4">Video Production</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-testid="content-creation-title">
              Content <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Creation</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Video content created for <a href="https://1staveicp.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 font-semibold">1staveicp.com</a> — used in Facebook campaigns and currently featured on their website.
            </p>
          </div>

          {/* Campaign Results Banner */}
          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 mb-10 shadow-xl" data-testid="campaign-results-banner">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="text-white text-center lg:text-left">
                <h3 className="text-2xl font-bold mb-2">1st Avenue Ice Cream & Pastry</h3>
                <p className="text-white/80 text-sm">Mineral Wells, TX • Facebook Video Campaign • 3-Month Results</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4">
                  <div className="text-3xl font-bold text-white">127K</div>
                  <div className="text-xs text-white/80 uppercase tracking-wide">Video Views</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4">
                  <div className="text-3xl font-bold text-white">+68%</div>
                  <div className="text-xs text-white/80 uppercase tracking-wide">Foot Traffic</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4">
                  <div className="text-3xl font-bold text-white">14.2%</div>
                  <div className="text-xs text-white/80 uppercase tracking-wide">Engagement</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4">
                  <div className="text-3xl font-bold text-white">2.8K</div>
                  <div className="text-xs text-white/80 uppercase tracking-wide">New Followers</div>
                </div>
              </div>
            </div>
          </div>

          {/* TikTok Influencer Highlight */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 mb-10 shadow-xl border border-gray-700" data-testid="tiktok-influencer-highlight">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 rounded-2xl flex items-center justify-center">
                  <Music className="text-white" size={32} />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-pink-400">Viral Moment</span>
                  <span className="text-xs bg-pink-500/20 text-pink-300 px-2 py-0.5 rounded-full">TikTok</span>
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Campaign Sparked TikTok Influencer Live Review</h4>
                <p className="text-gray-400 text-sm">Our video content caught the attention of a local TikTok influencer who did a live review of the shop — generating massive organic reach and driving even more customers to discover 1st Avenue Ice Cream.</p>
              </div>
              <div className="flex-shrink-0 text-center">
                <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-yellow-400">50K+</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Additional Views</div>
              </div>
            </div>
          </div>

          {/* Transferability to Mission-Driven Organizations */}
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-6 mb-10 border border-teal-200" data-testid="transferability-note">
            <div className="flex flex-col md:flex-row items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
                  <TrendingUp className="text-white" size={24} />
                </div>
              </div>
              <div>
                <h4 className="text-lg font-bold text-teal-900 mb-2">These Skills Transfer to Any Mission</h4>
                <p className="text-sm text-gray-700">The same video production, engagement strategy, and audience growth techniques that drove <strong>127K views and 68% foot-traffic lift</strong> for a local business are directly applicable to mission-driven organizations — from nonprofits and policy institutes to healthcare and education. Whether the goal is awareness, advocacy, or community engagement, I know how to turn complex stories into compelling content that moves people.</p>
              </div>
            </div>
          </div>

          {/* Video Grid - Optimized for Portrait Videos */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Video 1 - Weekly Drawing Promo */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-purple-100" data-testid="video-card-0">
              <div className="aspect-[9/16] bg-black flex items-center justify-center">
                <video 
                  controls 
                  className="w-full h-full object-contain"
                  poster=""
                  preload="metadata"
                  data-testid="video-0"
                >
                  <source src="https://customer-assets.emergentagent.com/job_sierra-anne-web/artifacts/foq0gb8t_Visit%201st%20Avenue%20Ice%20Cream%20and%20Pastry%20-%20Right%20across%20from%20the%20Baker%20Hotel%20-%20Enter%20our%20Weekly%20Drawing%20for%20FREE%20ice%20cream..mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-gray-900 text-sm">Weekly Drawing</h3>
                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Top</span>
                </div>
                <p className="text-xs text-gray-500">42.3K views • 18.4% eng.</p>
              </div>
            </div>

            {/* Video 2 - TV Ad Test */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-purple-100" data-testid="video-card-1">
              <div className="aspect-[9/16] bg-black flex items-center justify-center">
                <video 
                  controls 
                  className="w-full h-full object-contain"
                  poster=""
                  preload="metadata"
                  data-testid="video-1"
                >
                  <source src="https://customer-assets.emergentagent.com/job_sierra-anne-web/artifacts/p9kt6rcy_1staveicpTestTVAd.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-3">
                <h3 className="font-bold text-gray-900 text-sm mb-1">TV Advertisement</h3>
                <p className="text-xs text-gray-500">28.7K views • 12.1% eng.</p>
              </div>
            </div>

            {/* Video 3 - Death by Chocolate Stacker */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-purple-100" data-testid="video-card-2">
              <div className="aspect-[9/16] bg-black flex items-center justify-center">
                <video 
                  controls 
                  className="w-full h-full object-contain"
                  poster=""
                  preload="metadata"
                  data-testid="video-2"
                >
                  <source src="https://customer-assets.emergentagent.com/job_sierra-anne-web/artifacts/wmarfjvf_deathbychocolatestacker.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-gray-900 text-sm">Chocolate Stacker</h3>
                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">Viral</span>
                </div>
                <p className="text-xs text-gray-500">31.2K views • 15.8% eng.</p>
              </div>
            </div>

            {/* Video 4 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-purple-100" data-testid="video-card-3">
              <div className="aspect-[9/16] bg-black flex items-center justify-center">
                <video 
                  controls 
                  className="w-full h-full object-contain"
                  poster=""
                  preload="metadata"
                  data-testid="video-3"
                >
                  <source src="https://customer-assets.emergentagent.com/job_sierra-anne-web/artifacts/cslh93li_video2.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-3">
                <h3 className="font-bold text-gray-900 text-sm mb-1">Brand Highlight</h3>
                <p className="text-xs text-gray-500">14.6K views • 11.3% eng.</p>
              </div>
            </div>

            {/* Video 5 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-purple-100" data-testid="video-card-4">
              <div className="aspect-[9/16] bg-black flex items-center justify-center">
                <video 
                  controls 
                  className="w-full h-full object-contain"
                  poster=""
                  preload="metadata"
                  data-testid="video-4"
                >
                  <source src="https://customer-assets.emergentagent.com/job_sierra-anne-web/artifacts/ollbr7wj_video5.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-3">
                <h3 className="font-bold text-gray-900 text-sm mb-1">FB Campaign</h3>
                <p className="text-xs text-gray-500">10.2K views • 9.7% eng.</p>
              </div>
            </div>
          </div>

          {/* Business Results Banner */}
          <div className="mt-8 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 shadow-xl" data-testid="business-results-banner">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-white text-center md:text-left">
                <span className="text-xs font-semibold uppercase tracking-wider text-pink-400">Campaign Impact</span>
                <h3 className="text-xl font-bold mt-1">Business Results</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-400">+45%</div>
                  <div className="text-xs text-gray-400">Weekend Sales</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-400">+34</div>
                  <div className="text-xs text-gray-400">Google Reviews</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-400">+892</div>
                  <div className="text-xs text-gray-400">IG Followers</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-400">+22%</div>
                  <div className="text-xs text-gray-400">Avg. Order Value</div>
                </div>
              </div>
              <a 
                href="https://1staveicp.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 rounded-full text-sm font-semibold text-white transition-all"
                data-testid="visit-client-btn"
              >
                Visit Website
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="skills-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4" data-testid="skills-title">
            Tools & <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Proficiencies</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Experienced with industry-leading tools for social media marketing, design, analytics, and project management.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {skills.map((skillGroup, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100" data-testid={`skill-group-${index}`}>
                <div className="flex items-center gap-3 mb-4">
                  {index === 0 && <Target className="text-purple-600" size={24} />}
                  {index === 1 && <Palette className="text-pink-600" size={24} />}
                  {index === 2 && <TrendingUp className="text-blue-600" size={24} />}
                  {index === 3 && <Briefcase className="text-emerald-600" size={24} />}
                  <h3 className="text-lg font-bold text-gray-900">{skillGroup.category}</h3>
                </div>
                <div className="space-y-2">
                  {skillGroup.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="text-green-500" size={14} />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Google Ads / PPC */}
          <div className="mt-8 bg-gradient-to-r from-emerald-50 to-cyan-50 rounded-xl p-6 border border-emerald-200">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="text-emerald-600" size={24} />
              <h3 className="text-xl font-bold text-gray-900">Google Ads / PPC</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['Google Ads', 'Search Campaigns', 'Performance Max', 'Smart Bidding', 'Keyword Research', 'A/B Testing', 'Conversion Tracking', 'ROAS Optimization'].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle className="text-emerald-500" size={14} />
                  <span className="text-gray-700 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Microsoft 365 & Analytics */}
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <Monitor className="text-blue-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900">Microsoft 365 (Expert)</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {['Excel (Advanced)', 'PowerPoint', 'Word', 'Outlook', 'Teams', 'SharePoint', 'OneDrive', 'Data Analysis'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="text-blue-500" size={14} />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200">
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="text-orange-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900">Analytics & Reporting</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {['Google Analytics', 'Meta Business Suite', 'SEMrush', 'Campaign Reporting', 'KPI Tracking', 'ROI Analysis', 'A/B Testing', 'Data Visualization'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="text-orange-500" size={14} />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Blog Section */}
      <section id="social-media-tips" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="social-media-tips-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4" data-testid="blog-title">
            Latest <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Insights</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Thoughts on social media trends, strategies, and what's working today.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {[...dynamicBlogPosts, ...blogPosts].slice(0, 6).map((post, index) => (
              <div key={post.id || index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all" data-testid={`blog-post-${index}`}>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                    <span>{post.date}</span>
                    <span>•</span>
                    <span>{post.read_time || post.readTime}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900" data-testid={`blog-post-title-${index}`}>{post.title}</h3>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  <button
                    onClick={() => openBlogModal(post)}
                    className="text-purple-600 font-semibold hover:text-purple-700 flex items-center gap-2 transition-all"
                    data-testid={`blog-read-more-${index}`}
                  >
                    Read More
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Schedule Meet Section */}
      <section id="schedule-meet" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="schedule-meet-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4" data-testid="schedule-meet-title">
            Schedule a <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Live Meet</span>
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
            Ready to discuss how I can help grow your business? Book a free 30-minute consultation call. Available Monday - Friday, 9 AM - 7 PM CST.
          </p>
          
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100 shadow-lg">
            <iframe
              src="https://calendly.com/sierra-scala123/30min?hide_gdpr_banner=1"
              width="100%"
              height="700"
              frameBorder="0"
              title="Schedule a meeting with Sierra Scala"
              className="rounded-lg"
              data-testid="calendly-embed"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="contact-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4" data-testid="contact-title">
            Let's <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Connect</span>
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Ready to elevate your social media presence? Let's talk about how I can help your brand grow.
          </p>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-4">Get in Touch</h3>
              <p className="text-gray-600 mb-6">
                I'm currently seeking social media marketing opportunities where I can bring my creativity, strategic thinking, and proven track record to drive real results.
              </p>

              <div className="space-y-4">
                <a href="mailto:me@sierrascala.com" className="flex items-center gap-3 text-gray-700 hover:text-purple-600 transition-colors" data-testid="contact-email">
                  <Mail size={20} />
                  <span>me@sierrascala.com</span>
                </a>
                <a href="https://www.linkedin.com/in/sierra-scala-62b9a0254" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-700 hover:text-purple-600 transition-colors" data-testid="contact-linkedin">
                  <Linkedin size={20} />
                  <span>LinkedIn Profile</span>
                </a>
              </div>

              <a
                href="/Sierra_Scala_Resume.pdf"
                download="Sierra_Scala_Resume.pdf"
                className="mt-8 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-semibold hover:shadow-lg transition-all flex items-center gap-2 inline-flex"
                data-testid="download-resume-button"
              >
                <Download size={20} />
                Download Resume
              </a>
            </div>

            {/* Role Selection */}
            {!formType && (
              <div className="space-y-4" data-testid="role-selection">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">I am a:</h3>

                <label
                  className="flex items-start gap-4 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-purple-400 transition-all"
                  data-testid="employer-option"
                >
                  <input
                    type="radio"
                    name="roleType"
                    value="employer"
                    onChange={(e) => setFormType(e.target.value)}
                    className="mt-1 w-5 h-5 text-purple-600 focus:ring-purple-500"
                  />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Direct Employer</div>
                    <div className="text-sm text-gray-600">I work for the company and am hiring directly</div>
                  </div>
                </label>

                <label
                  className="flex items-start gap-4 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-purple-400 transition-all"
                  data-testid="recruiter-option"
                >
                  <input
                    type="radio"
                    name="roleType"
                    value="recruiter"
                    onChange={(e) => setFormType(e.target.value)}
                    className="mt-1 w-5 h-5 text-purple-600 focus:ring-purple-500"
                  />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Recruiter / Headhunter</div>
                    <div className="text-sm text-gray-600">I'm recruiting on behalf of a client company</div>
                  </div>
                </label>
              </div>
            )}

            {/* Contact Form - Shows after selection */}
            {formType && (
              <>
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full"></div>
                    <span className="text-sm font-semibold text-gray-700">
                      {formType === 'employer' ? 'Direct Employer Form' : 'Recruiter Form'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormType('')}
                    className="text-sm text-purple-600 hover:text-purple-700 underline"
                    data-testid="change-role-button"
                  >
                    Change Role
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Your full name"
                      data-testid="contact-form-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="your@email.com"
                      data-testid="contact-form-email"
                    />
                  </div>

                  {formType === 'employer' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Your company name"
                          data-testid="contact-form-company"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Your Job Title</label>
                        <input
                          type="text"
                          name="yourTitle"
                          value={formData.yourTitle}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="e.g., Marketing Director, HR Manager"
                          data-testid="contact-form-your-title"
                        />
                      </div>
                    </>
                  )}

                  {formType === 'recruiter' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Recruiting Agency Name</label>
                        <input
                          type="text"
                          name="agencyName"
                          value={formData.agencyName}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Your recruiting agency"
                          data-testid="contact-form-agency"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Client Company</label>
                        <input
                          type="text"
                          name="clientCompany"
                          value={formData.clientCompany}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Company you're recruiting for"
                          data-testid="contact-form-client-company"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Position Title</label>
                    <input
                      type="text"
                      name="hiringPosition"
                      value={formData.hiringPosition}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="e.g., Social Media Manager"
                      data-testid="contact-form-hiring-position"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Position Type</label>
                    <select
                      name="positionType"
                      value={formData.positionType}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                      data-testid="contact-form-position-type"
                    >
                      <option value="">Select position type</option>
                      <option value="Remote">Remote</option>
                      <option value="Onsite">Onsite (Dallas - Fort Worth only)</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Details</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      required
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Tell me about the role..."
                      data-testid="contact-form-message"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="contact-form-submit"
                  >
                    {formStatus === 'sending' ? 'Sending...' : (
                      <>
                        Send Message
                        <Send size={20} />
                      </>
                    )}
                  </button>

                  {formStatus === 'success' && (
                    <div className="text-green-600 text-center font-semibold" data-testid="form-success-message">
                      ✓ Message sent successfully!
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="text-red-600 text-center font-semibold" data-testid="form-error-message">
                      ✗ Failed to send message. Please try again.
                    </div>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-6xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Sierra Scala</h3>
          <p className="text-gray-400 mb-6">Social Media Marketing & Google PPC Specialist</p>
          <div className="flex justify-center gap-6 mb-6">
            <a href="https://www.linkedin.com/in/sierra-scala-62b9a0254" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors" data-testid="footer-linkedin">
              <Linkedin size={24} />
            </a>
          </div>
          {/* Share QR Code */}
          <div className="mb-6" data-testid="share-qr-code">
            <p className="text-gray-300 text-sm font-semibold mb-3">Share with Friends</p>
            <div className="inline-block bg-white p-3 rounded-xl">
              <img
                src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https://sierrascala.com&bgcolor=ffffff&color=1a1a2e&format=svg"
                alt="Scan to visit sierrascala.com"
                width={120}
                height={120}
                className="block"
              />
            </div>
            <p className="text-gray-500 text-xs mt-2">sierrascala.com</p>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Like what you see? Get your own.{' '}
            <a
              href="https://web-techservices.com/personal-portfolio?ref=H8KAAILH"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 transition-colors underline"
            >
              Click here
            </a>
          </p>
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Sierra Scala. Web Design by{' '}
            <a
              href="https://web-techservices.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 transition-colors underline"
            >
              Web-Techservices.com
            </a>
            <span className="mx-2 text-gray-700">|</span>
            <a href="/admin" className="text-gray-700 hover:text-gray-500 transition-colors" data-testid="admin-link">Admin</a>
          </p>
        </div>
      </footer>

      {/* Blog Modal */}
      {isBlogModalOpen && selectedBlogPost && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-0 md:p-4"
          onClick={closeBlogModal}
          data-testid="blog-modal-overlay"
        >
          <div
            className="bg-white rounded-none md:rounded-2xl max-w-4xl w-full h-full md:h-auto md:max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="blog-modal-content"
          >
            <button
              onClick={closeBlogModal}
              className="sticky top-4 right-4 float-right bg-white rounded-full p-2 md:p-3 shadow-lg hover:bg-gray-100 transition-all z-10"
              aria-label="Close article"
              data-testid="blog-modal-close"
            >
              <X size={20} className="text-gray-600 md:w-6 md:h-6" />
            </button>

            <div className="p-6 md:p-12">
              <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                <span>{selectedBlogPost.date}</span>
                <span>•</span>
                <span>{selectedBlogPost.readTime}</span>
              </div>

              <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {selectedBlogPost.title}
              </h2>

              <div
                className="prose prose-lg max-w-none"
                style={{
                  color: '#374151',
                  lineHeight: '1.8'
                }}
                dangerouslySetInnerHTML={{ __html: selectedBlogPost.content }}
              />

              <div className="mt-8 pt-8 border-t border-gray-200">
                <button
                  onClick={closeBlogModal}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                  data-testid="blog-modal-close-button"
                >
                  Close Article
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exit Intent Popup */}
      {showExitPopup && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowExitPopup(false)}
          data-testid="exit-popup-overlay"
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full p-8 relative shadow-2xl transform animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            data-testid="exit-popup-content"
          >
            <button
              onClick={() => setShowExitPopup(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-all"
              aria-label="Close popup"
              data-testid="exit-popup-close"
            >
              <X size={24} className="text-gray-500" />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="text-white" size={32} />
              </div>
              
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Wait! Before You Go...
              </h3>
              
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                If you like my personal portfolio website, just imagine what I can do to help <span className="font-semibold text-purple-600">your business</span> grow!
              </p>
              
              <p className="text-gray-500 mb-8">
                Don't forget to fill out the form below and let's connect!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                <button
                  onClick={() => {
                    setShowExitPopup(false);
                    scrollToSection('contact');
                  }}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all flex items-center justify-center gap-2"
                  data-testid="exit-popup-cta"
                >
                  Let's Connect
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={() => setShowExitPopup(false)}
                  className="px-8 py-3 border-2 border-gray-300 text-gray-600 rounded-full font-semibold hover:bg-gray-50 transition-all"
                  data-testid="exit-popup-dismiss"
                >
                  Maybe Later
                </button>
              </div>

              <p className="text-base text-gray-600 font-medium">
                Like this website? <a href="https://web-techservices.com/personal-portfolio?ref=H8KAAILH" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline font-semibold">Get your own custom design here</a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default PortfolioApp;
